import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AccordionModule } from 'primeng/accordion';
import { MessageModule } from 'primeng/message';
import { MessageService } from 'primeng/api';
import { MarkdownPipe } from '@/shared/pipes/markdown.pipe';
import { CloudApiService, PackageLinkResolution } from '@/cloud/services/cloud-api.service';
import { clampProgramSlot, fgetfileCommand, progloadCommand } from '@/cloud/services/console-commands';
import { TranslationService } from '@/layout/service/translation.service';
import { AppFloatingConfigurator } from '@/layout/component/app.floatingconfigurator';

type LinkPageState = 'loading' | 'ready' | 'not-found' | 'expired' | 'unavailable';

/**
 * Public instruction page opened by a short package link (https://host/p/{code}).
 * It resolves the link to a fresh storage URL and shows the console commands together with
 * the moment by which the download must be started. No sign-in is required.
 */
@Component({
    selector: 'app-package-link-page',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink, ButtonModule, InputTextModule, TagModule, TooltipModule, ProgressSpinnerModule, AccordionModule, MessageModule, MarkdownPipe, AppFloatingConfigurator],
    templateUrl: './package-link-page.html',
    styleUrl: './package-link-page.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PackageLinkPage implements OnInit {
    private api = inject(CloudApiService);
    private route = inject(ActivatedRoute);
    private messageService = inject(MessageService);
    private destroyRef = inject(DestroyRef);
    readonly t = inject(TranslationService).t;

    readonly code = signal('');
    readonly state = signal<LinkPageState>('loading');
    readonly link = signal<PackageLinkResolution | null>(null);
    readonly expiredAtUtc = signal<string | null>(null);
    readonly programSlot = signal(1);
    /** Ticks once a second while a download URL is on screen so the countdown stays live. */
    readonly now = signal(Date.now());

    readonly deadline = computed(() => {
        const link = this.link();
        return link ? new Date(link.downloadExpiresUtc).getTime() : 0;
    });
    readonly remainingMs = computed(() => Math.max(0, this.deadline() - this.now()));
    readonly downloadExpired = computed(() => this.state() === 'ready' && this.remainingMs() <= 0);
    readonly remainingText = computed(() => {
        const total = Math.ceil(this.remainingMs() / 1000);
        const hours = Math.floor(total / 3600);
        const minutes = Math.floor((total % 3600) / 60);
        const seconds = total % 60;
        const pad = (value: number) => String(value).padStart(2, '0');
        return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
    });
    readonly fgetfile = computed(() => (this.downloadExpired() ? '' : fgetfileCommand(this.link()?.downloadUrl, this.programSlot())));
    readonly progload = computed(() => progloadCommand(this.programSlot()));

    private request = 0;
    private timer: ReturnType<typeof setInterval> | null = null;

    constructor() {
        this.destroyRef.onDestroy(() => {
            ++this.request;
            this.stopTimer();
        });
    }

    ngOnInit(): void {
        this.route.paramMap.subscribe((params) => {
            this.code.set(params.get('code') ?? '');
            void this.load();
        });
    }

    async load(): Promise<void> {
        const request = ++this.request;
        this.stopTimer();
        this.state.set('loading');
        this.link.set(null);
        try {
            const resolved = await this.api.resolvePackageLink(this.code());
            if (request !== this.request) return;
            this.link.set(resolved);
            this.now.set(Date.now());
            this.state.set('ready');
            this.startTimer();
        } catch (error) {
            if (request !== this.request) return;
            const status = error instanceof HttpErrorResponse ? error.status : 0;
            if (status === 410) {
                this.expiredAtUtc.set((error as HttpErrorResponse).error?.expiredUtc ?? null);
                this.state.set('expired');
            } else if (status === 404) {
                this.state.set('not-found');
            } else {
                this.state.set('unavailable');
            }
        }
    }

    setSlot(value: number | string | null): void {
        this.programSlot.set(clampProgramSlot(typeof value === 'string' ? parseInt(value, 10) : value));
    }

    copy(text: string): void {
        if (!text) return;
        navigator.clipboard
            .writeText(text)
            .then(() => this.messageService.add({ severity: 'success', summary: this.t()('linkPage.copied'), life: 2000 }))
            .catch(() => this.messageService.add({ severity: 'error', summary: this.t()('linkPage.copyFailed'), life: 3000 }));
    }

    formatDate(value: string | null | undefined): string {
        if (!value) return '';
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
    }

    formatSize(bytes: number): string {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    fill(key: string, values: Record<string, string | number>): string {
        return Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, String(value)), this.t()(key));
    }

    private startTimer(): void {
        this.timer = setInterval(() => {
            this.now.set(Date.now());
            if (this.remainingMs() <= 0) this.stopTimer();
        }, 1000);
    }

    private stopTimer(): void {
        if (this.timer !== null) {
            clearInterval(this.timer);
            this.timer = null;
        }
    }
}
